/**
 * Naming contract for list filters persisted in the browser, and their cleanup.
 *
 * Filters are persisted under two mechanisms:
 * - localStorage, by `FiltersContextFactory` urlSync (`<feature>-filters-v<N>`);
 * - cookies, by `filter-cookie` on the accounting pages (`<page>_filters`).
 *
 * Both key types are enforced by the type checker, so any new persisted filter
 * follows the convention and is cleared on logout without maintaining a list.
 * Other persisted data (auth token, column layout, theme, hints) never matches.
 */

export type FiltersStorageKey = `${string}-filters-v${number}`;
export type FiltersCookieKey = `${string}_filters`;

const FILTERS_STORAGE_KEY_PATTERN = /-filters-v\d+$/;
const FILTERS_COOKIE_KEY_PATTERN = /_filters$/;

const listLocalStorageKeys = (): string[] =>
  Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
    .filter((key): key is string => key !== null);

const listCookieNames = (): string[] =>
  document.cookie
    .split(";")
    .map((chunk) => chunk.trim().split("=")[0])
    .filter((name) => name.length > 0);

const clearFiltersLocalStorage = (): void => {
  listLocalStorageKeys()
    .filter((key) => FILTERS_STORAGE_KEY_PATTERN.test(key))
    .forEach((key) => localStorage.removeItem(key));
};

const clearFiltersCookie = (name: string): void => {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
};

const clearFiltersCookies = (): void => {
  listCookieNames()
    .filter((name) => FILTERS_COOKIE_KEY_PATTERN.test(name))
    .forEach(clearFiltersCookie);
};

// URL param keys holding filters, per pathname. Registered by the pages that
// mirror their filters in the URL, and kept after unmount: on logout the page
// unmounts before the return URL is built.
const filterUrlKeysByPathname = new Map<string, Set<string>>();

export const registerFilterUrlKeys = (pathname: string, keys: Iterable<string>): void => {
  const registeredKeys = filterUrlKeysByPathname.get(pathname) ?? new Set<string>();
  for (const key of keys) registeredKeys.add(key);
  filterUrlKeysByPathname.set(pathname, registeredKeys);
};

/**
 * Removes the registered filter params from `search` for `pathname`, keeping
 * the other params (tab, id…). Returns a search string with its leading "?",
 * or "" when no param is left.
 */
export const removeFilterParams = (pathname: string, search: string): string => {
  const filterKeys = filterUrlKeysByPathname.get(pathname);
  if (!filterKeys) return search;
  const params = new URLSearchParams(search);
  filterKeys.forEach((key) => params.delete(key));
  const remainingSearch = params.toString();
  return remainingSearch ? `?${remainingSearch}` : "";
};

/**
 * Removes the given persisted filters only, so a page reads its filters from
 * the URL it is opened on (saved view applied, YTI-1926).
 */
export const clearPersistedFilterKeys = (keys: readonly (FiltersStorageKey | FiltersCookieKey)[]): void => {
  if (typeof window === "undefined") return;
  try {
    keys.forEach((key) => {
      if (FILTERS_STORAGE_KEY_PATTERN.test(key)) localStorage.removeItem(key);
      else clearFiltersCookie(key);
    });
  }
  catch {
    // Storage disabled or unavailable: nothing persisted to clear.
  }
};

/**
 * Removes every persisted list filter (localStorage + cookies). Called when the
 * session ends so the next login starts from the default filters.
 */
export const clearPersistedFilters = (): void => {
  if (typeof window === "undefined") return;
  try {
    clearFiltersLocalStorage();
    clearFiltersCookies();
  }
  catch {
    // Storage disabled or unavailable: nothing persisted to clear, and logout
    // must never fail because of it.
  }
};
